import { DefinedVariable, LogicPagePayload } from "./allData";
import { FormWrapper } from "./FormWrapper";
import QueryChainManager from "./QueryChainManager";
import React, { useState } from "react";
import {
  CirclePlus,
  Trash,
  SquarePen,
  FolderOpen,
  Trash2,
  Group,
} from "lucide-react";
import CreateBundleDialog from "./CreateBundleDialog";
import { toast } from "@components/ui/use-toast";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@components/ui/tabs";
import { Button } from "@components/ui/button";
import {
  commonDeleteApi,
  deleteDatalistMapping,
  deleteSaveLogMapping,
  fetchLogicPageInfo,
  saveLogicPageInfo,
} from "./createPluginApi";
import { useMutation, useQuery, useQueryClient } from "react-query";
import PageLoader from "@components/loaders/PageLoader";
import Confirmation from "@components/common/ConfirmDialog";
import OtherLogicTypeDialog from "./OtherLogicTypeDialog";
import InfoTooltip from "@components/common/InfoTooltip";
import ViewMappingsDialog from "./ViewDatalistDialog";
import FallBackUi from "./FallBackUi";
import {
  getBundleTypeEnum,
  sortLogicTypes,
  validateBodyContent,
  mapAuthTypeToBackend,
} from "./logicUtils";
import {
  useMeshPluginContext,
  MeshPluginActions,
} from "@services/providers/MeshPlugin";

type LogicData = {
  goToPage?: (index: number) => void;
  setSelectedApiData?: (data: any) => void;
  selectedApiData?: any;
  setLogicType?: (type: any) => void;
  logicType?: any[];
};

function LogicPage({
  goToPage,
  setSelectedApiData,
  selectedApiData,
  logicType,
  setLogicType,
}: LogicData) {
  const {
    pluginId,
    uiPages,
    definedVariables,
    isSavedSection,
    configurationData,
    selectedLogicName,
    dispatch,
    hasLogicPage,
  } = useMeshPluginContext();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [croneBundleTime, setCroneBundleTime] = useState("");
  const [bundleDependency, setBundleDependency] = useState<string>("");
  const [selectedLogicBlockId, setSelectedLogicBlockId] = useState<
    number | null
  >(null);
  const [showAddLogicForm, setShowAddLogicForm] = useState(false);
  const [mappingSaveData, setMappingSaveData] = useState<any[]>([]);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [deleteBundleId, setDeleteBundleId] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [openThreadMeshLookUp, setOpenThreadMeshLookUp] = useState(false);
  const [threatLookupMap, setThreatLookupMap] = useState<
    Record<number, string>
  >({});
  const [otherType, setOtherType] = useState<string | null>(null);
  const [bundleEditId, setBundleEditId] = useState<number | null>(null);
  const [deleteLogicTypeName, setDeleteLogicTypeName] = useState<string | null>(
    null,
  );

  const [openViewDatalist, setOpenViewDatalist] = useState(false);
  const [viewDatalistData, setViewDatalistData] = useState<any[]>([]);
  const [viewSaveLogsData, setViewSaveLogsData] = React.useState<any[]>([]);

  const [isDeleteLogicType, setIsDeleteLogicType] = useState<boolean>(false);
  const [dListItems, setDListItems] = React.useState<any[]>([]);
  const pendingSelectedLogicNameRef = React.useRef<string | null>(null);

  const queryClient = useQueryClient();

  // States for datalist/mapping deletion confirmation
  const [isDeleteDatalistConfirmOpen, setIsDeleteDatalistConfirmOpen] =
    useState<boolean>(false);
  const [pendingDeleteTitle, setPendingDeleteTitle] = useState<string | null>(
    null,
  );
  const [pendingDeleteLogUid, setPendingDeleteLogUid] = useState<string | null>(
    null,
  );

  const currentLogic: any = logicType?.find(
    (lt) => lt.name === selectedLogicName,
  );

  // Check if the logic section is already saved
  const alreadySaved =
    isSavedSection?.find((section) => section.name === "logic")?.isSaved ||
    false;

  // const isData = logicType?.find((lt) => lt.name === selectedLogicName)?.data?.length === 0;

  // Check if we're in edit mode
  const isEditMode = React.useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("edit") === "1";
  }, []);

  const { data: fetchedLogicPageData } = useQuery({
    queryKey: ["fetchLogicPageData", pluginId],
    queryFn: () => fetchLogicPageInfo(pluginId!),
    enabled: !!pluginId && isEditMode && hasLogicPage,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });

  // set the datalist items from fetched logic page data
  React.useEffect(() => {
    if (!fetchedLogicPageData?.data) {
      setDListItems([]);
      return;
    }

    const dataListItemsFromFetch =
      fetchedLogicPageData?.data?.LogicBundles?.filter(
        (bundle: any) => bundle?.bundleType === "datalist",
      ) || [];

    setDListItems(dataListItemsFromFetch);
  }, [fetchedLogicPageData]);

  // merge the fetched datalist and mappingSaveData to show in view datalist dialog
  React.useEffect(() => {
    const mergedMap = new Map<string, any>();
    (fetchedLogicPageData?.data?.LogicBundles || [])
      .filter((bundle: any) => bundle.bundleType === "datalist")
      .forEach((bundle: any) => {
        (bundle.datalists || []).forEach((dl: any) => {
          if (!dl.datalistUid) return;

          mergedMap.set(dl.datalistUid, {
            datalistUid: dl.datalistUid,
            dataTitile: dl.dataTitile,
            cronTimeType: dl.cronTimeType,
            logType: dl.logType,
            typeDatalist: dl.typeDatalist,
            requestId: dl.requestId,
            fields: (dl.mappingDataList || []).map((m: any) => ({
              fieldId: m.fieldId,
              rawFieldName: m.rawFieldName,
              mappingLsField: m.mappingLsField,
              column_type: m.column_type,
              data_type: m.data_type,
              display_name:
                m.display_name ??
                (m.mappingLsField
                  ? m.mappingLsField
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (c: string) => c.toUpperCase())
                  : ""),
            })),
          });
        });
      });
    Object.values(mappingSaveData || {})
      .flat()
      .filter((m: any) => m.logType === "datalist")
      .forEach((m: any) => {
        const uid = m.logUid || `${m.dataListName}_${m.bundleId || "local"}`;

        mergedMap.set(uid, {
          datalistUid: uid,
          dataTitile: m.dataListName || "",
          cronTimeType: m.cronType,
          logType: m.logType,
          typeDatalist: m.typeDatalist,
          requestId: m.requestId,
          fields: (m.mappingFields || []).map((f: any) => ({
            fieldId: f.fieldId,
            rawFieldName: f.rawFieldName,
            mappingLsField: f.mappingLsField,
            column_type: f.column_type,
            data_type: f.data_type,
            display_name: f.display_name,
          })),
        });
      });
    setViewDatalistData(Array.from(mergedMap.values()));
  }, [fetchedLogicPageData, mappingSaveData]);

  // Merge fetched and local Save Logs data for viewing in the ViewDatalistDialog
  React.useEffect(() => {
    const mergedMap = new Map<string, any>();

    //  BACKEND (saved) Save Logs
    (fetchedLogicPageData?.data?.LogicBundles || [])
      .filter((bundle: any) => bundle.bundleType === "parquet")
      .forEach((bundle: any) => {
        (bundle.mappings || []).forEach((m: any) => {
          if (!m.logUid) return;

          mergedMap.set(m.logUid, {
            uid: m.logUid,
            title: m.logTitile || bundle.bundleTitle || "Save Logs",
            subtitle: `Category: ${m.logCategory} • Request ${m.requestId}`,
            fields: (m.mappingFields || []).map((f: any) => ({
              fieldId: f.fieldId,
              rawFieldName: f.rawFieldName,
              mappingLsField: f.mappingLsField,
              display_name: f.display_name,
              type: f.type,
              category: f.category,
            })),
          });
        });
      });

    //  LOCAL (unsaved) Save Logs
    Object.values(mappingSaveData || {})
      .flat()
      .filter((m: any) => m.logType === "parquet")
      .forEach((m: any) => {
        if (!m.mappingFields?.length) return;

        const uid = m.logUid || `local_parquet_${m.bundleId}_${m.requestId}`;

        mergedMap.set(uid, {
          uid,
          title: m.logTitile || "Save Logs (Unsaved)",
          subtitle: `Category: ${m.logCategory} • Request ${m.requestId}`,
          fields: m.mappingFields.map((f: any) => ({
            fieldId: f.fieldId,
            rawFieldName: f.rawFieldName,
            mappingLsField: f.mappingLsField,
            display_name: f.display_name,
            type: f.type,
            category: f.category,
          })),
        });
      });

    setViewSaveLogsData(Array.from(mergedMap.values()));
  }, [fetchedLogicPageData, mappingSaveData, isDeleteDatalistConfirmOpen]);

  // find threat lookup bundle from fetched data
  const threatLookUpBundle = fetchedLogicPageData?.data?.LogicBundles?.find(
    (bundle: any) => bundle.bundleType === "threat_lookup",
  );

  const dataListBundles = dListItems;

  // Helper function to convert backend pagination to frontend paginationFormData
  const convertPaginationToFormData = (pagination: any) => {
    if (!pagination || !pagination.type || pagination.type === "none") {
      return [];
    }

    const param = pagination.parameter || {};
    const formDataRow: any = {
      paginationEnable: true,
    };

    switch (pagination.type) {
      case "offset_limit":
      case "offset_limit_pagination":
        // Map to categoryTypeKey/Value (for offset) and paginationTypeKey/Value (for limit)
        formDataRow.categoryTypeKey = param.offset_key || "";
        formDataRow.categoryTypeValue = param.offset_value || "0";
        formDataRow.paginationTypeKey = param.limit_key || "";
        formDataRow.paginationTypeValue = param.limit_value || "100";
        break;

      case "page_page_size":
      case "page_size_pagination":
        // Map to categoryTypeKey/Value (for page) and paginationTypeKey/Value (for page size)
        formDataRow.categoryTypeKey = param.page_key || "";
        formDataRow.categoryTypeValue = param.page_value || "1";
        formDataRow.paginationTypeKey =
          param.per_page_key || param.page_size_key || "";
        formDataRow.paginationTypeValue =
          param.per_page_value || param.page_size_value || "100";
        break;

      case "cursor":
      case "cursor_pagination":
        formDataRow.limitKey = param.limit_key || "";
        formDataRow.limitValue = param.limit_value || "";
        formDataRow.cursorKey = param.cursor_key || "";
        formDataRow.cursorValue = param.cursor_value || "";
        formDataRow.cursorSourcePath = param.cursor_source_path || "";
        break;

      case "token":
      case "token_pagination":
        formDataRow.limitKey = param.limit_key || "";
        formDataRow.limitValue = param.limit_value || "";
        formDataRow.tokenKey = param.token_key || "";
        formDataRow.tokenValue = param.token_value || "";
        formDataRow.tokenSourcePath = param.token_source_path || "";
        break;

      case "next_url":
      case "next_url_pagination":
        formDataRow.limitKey = param.limit_key || "";
        formDataRow.limitValue = param.limit_value || "";
        formDataRow.nextUrlKey = param.next_url_key || "";
        formDataRow.nextUrlValue = ""; // Next URL comes from API response
        formDataRow.nextUrlSourcePath = ""; // Response path not needed
        break;

      default:
        return [];
    }

    return [formDataRow];
  };

  // update the logicType if threat lookup bundle there found in fetched data
  React.useEffect(() => {
    if (!dataListBundles || !setLogicType) return;
    if (dataListBundles.length === 0) {
      setLogicType?.((prev: any[]) =>
        prev.filter((item) => item.name !== "Data List"),
      );
      return;
    }

    setLogicType?.((prev: any[]) => {
      const existing = prev.find((p) => p.name === "Data List");

      const backendBundles = dataListBundles.map((bundle: any) => ({
        id: bundle.bundleId,
        title: bundle.bundleTitle || "Data List Bundle",
        description: bundle.bundleDescription || "",
        bundleCronTime: bundle.bundleCronTime || "",
        bundleDependency: bundle.bundleDependency || null,
        bundleActivate: bundle.bundleActivate ?? true,
        blocks: bundle.requests.map((req: any) => ({
          id: req.requestId,
          response: "",
        })),
        finalRequestData: {
          request: (bundle.requests || []).map((req: any) => {
            // Map backend auth type to frontend format
            let authType = req.auth?.type || "none";
            if (authType === "basic_auth") authType = "basic";
            else if (authType === "bearer_token") authType = "bearer";
            else if (authType === "jwt_token") authType = "jwtBearer";
            else if (authType === "api_key") authType = "api-key";
            // authorization stays as authorization

            return {
              ...req,
              auth: {
                ...req.auth,
                type: authType,
              },
              bodyType:
                req.body_type === "x-www-form-urlencoded"
                  ? "form-urlencoded"
                  : req.body_type || "none",
              rawBodyType: req.raw_type || "text",
              body:
                req.body_type === "x-www-form-urlencoded" &&
                typeof req.body === "object"
                  ? ""
                  : req.body || "",
              formData:
                req.body_type === "x-www-form-urlencoded" &&
                typeof req.body === "object"
                  ? Object.entries(req.body || {}).map(([key, value]) => ({
                      key,
                      value: String(value),
                      enabled: true,
                    }))
                  : [],
              paginationType: req.pagination?.type || "none",
              paginationFormData: convertPaginationToFormData(req.pagination),
              filteration: req.filteration || {
                type: "none",
                default_cron_start_time: "none",
                start_key: "",
                end_key: "",
                custom_date_format: "",
              },
            };
          }),
          variables: [],
          responses: {},
        },
        datalists: bundle.datalists || [],
        response: {},
        isResponse: false,
      }));

      if (!existing) {
        const updated = [...prev, { name: "Data List", data: backendBundles }];
        // Sort to maintain consistent order: Action, Save Logs, Settings, then by min bundleId
        return sortLogicTypes(updated);
      }

      const updated = prev.map((item) =>
        item.name === "Data List" ? { ...item, data: backendBundles } : item,
      );
      return sortLogicTypes(updated);
    });
  }, [dataListBundles, setLogicType]);

  // Update Settings bundles from fetched data
  React.useEffect(() => {
    if (!fetchedLogicPageData?.data?.LogicBundles || !setLogicType) return;

    const settingsBundles = fetchedLogicPageData.data.LogicBundles.filter(
      (bundle: any) => bundle.bundleType === "setting",
    );

    if (settingsBundles.length === 0) return;

    setLogicType((prev: any[]) => {
      const settingsLogic = prev.find((item) => item.name === "Settings");
      if (!settingsLogic) return prev;

      // Update bundleActivate for existing Settings bundles
      const updatedSettingsData = settingsLogic.data.map((localBundle: any) => {
        const fetchedBundle = settingsBundles.find(
          (fb: any) => fb.bundleId === localBundle.id,
        );

        if (fetchedBundle) {
          return {
            ...localBundle,
            bundleActivate: fetchedBundle.bundleActivate ?? true,
          };
        }
        return localBundle;
      });

      return prev.map((item) =>
        item.name === "Settings"
          ? { ...item, data: updatedSettingsData }
          : item,
      );
    });
  }, [fetchedLogicPageData, setLogicType]);

  // Update Action bundles from fetched data
  React.useEffect(() => {
    if (!fetchedLogicPageData?.data?.LogicBundles || !setLogicType) return;

    const actionBundles = fetchedLogicPageData.data.LogicBundles.filter(
      (bundle: any) => bundle.bundleType === "action",
    );

    if (actionBundles.length === 0) return;

    setLogicType((prev: any[]) => {
      const actionLogic = prev.find((item) => item.name === "Action");
      if (!actionLogic) return prev;

      // Update bundleActivate for existing Action bundles
      const updatedActionData = actionLogic.data.map((localBundle: any) => {
        const fetchedBundle = actionBundles.find(
          (fb: any) => fb.bundleId === localBundle.id,
        );

        if (fetchedBundle) {
          return {
            ...localBundle,
            bundleActivate: fetchedBundle.bundleActivate ?? true,
          };
        }
        return localBundle;
      });

      return prev.map((item) =>
        item.name === "Action" ? { ...item, data: updatedActionData } : item,
      );
    });
  }, [fetchedLogicPageData, setLogicType]);

  // Update Save Logs bundles from fetched data
  React.useEffect(() => {
    if (!fetchedLogicPageData?.data?.LogicBundles || !setLogicType) return;

    const saveLogsBundles = fetchedLogicPageData.data.LogicBundles.filter(
      (bundle: any) => bundle.bundleType === "parquet",
    );

    if (saveLogsBundles.length === 0) return;

    setLogicType((prev: any[]) => {
      const saveLogsLogic = prev.find((item) => item.name === "Save Logs");
      if (!saveLogsLogic) return prev;

      // Fully restore Save Logs bundles with all request data (similar to Data List)
      const backendBundles = saveLogsBundles.map((bundle: any) => ({
        id: bundle.bundleId,
        title: bundle.bundleTitle || "Save Logs Bundle",
        description: bundle.bundleDescription || "",
        bundleCronTime: bundle.bundleCronTime || "",
        bundleDependency: bundle.bundleDependency || null,
        bundleActivate: bundle.bundleActivate ?? true,
        blocks: bundle.requests.map((req: any) => ({
          id: req.requestId,
          response: "",
        })),
        finalRequestData: {
          request: (bundle.requests || []).map((req: any) => {
            // Map backend auth type to frontend format
            let authType = req.auth?.type || "none";
            if (authType === "basic_auth") authType = "basic";
            else if (authType === "bearer_token") authType = "bearer";
            else if (authType === "jwt_token") authType = "jwtBearer";
            else if (authType === "api_key") authType = "api-key";

            return {
              ...req,
              auth: {
                ...req.auth,
                type: authType,
              },
              bodyType:
                req.body_type === "x-www-form-urlencoded"
                  ? "form-urlencoded"
                  : req.body_type || "none",
              rawBodyType: req.raw_type || "text",
              body:
                req.body_type === "x-www-form-urlencoded" &&
                typeof req.body === "object"
                  ? ""
                  : req.body || "",
              formData:
                req.body_type === "x-www-form-urlencoded" &&
                typeof req.body === "object"
                  ? Object.entries(req.body || {}).map(([key, value]) => ({
                      key,
                      value: String(value),
                      enabled: true,
                    }))
                  : [],
              paginationType: req.pagination?.type || "none",
              paginationFormData: convertPaginationToFormData(req.pagination),
              queryParams: req.queryParams || [],
              filteration: req.filteration || {
                type: "none",
                default_cron_start_time: "none",
                start_key: "",
                end_key: "",
                custom_date_format: "",
              },
            };
          }),
          variables: [],
          responses: {},
        },
        mappings: bundle.mappings || [],
        response: {},
        isResponse: false,
      }));

      // Merge backend bundles with local unsaved bundles
      const localUnsavedBundles = saveLogsLogic.data.filter(
        (localBundle: any) =>
          !saveLogsBundles.some((fb: any) => fb.bundleId === localBundle.id),
      );

      const mergedData = [...backendBundles, ...localUnsavedBundles];

      return prev.map((item) =>
        item.name === "Save Logs" ? { ...item, data: mergedData } : item,
      );
    });
  }, [fetchedLogicPageData, setLogicType]);

  // threat lookup logic type and bundle addition
  React.useEffect(() => {
    if (!setLogicType) return;

    if (!threatLookUpBundle) {
      setLogicType((prev: any[]) =>
        prev.filter((item) => item.name !== "Threat Lookup"),
      );
      return;
    }

    setLogicType((prev: any[]) => {
      const exists = prev.some((item) => item.name === "Threat Lookup");

      if (exists) {
        //  If Threat Lookup already exists, update its data
        return prev.map((item) => {
          if (item.name === "Threat Lookup") {
            return {
              ...item,
              data: [
                {
                  id: threatLookUpBundle.bundleId,
                  title:
                    threatLookUpBundle.bundleTitle || "Threat Lookup Bundle",
                  description: threatLookUpBundle.bundleDescription || "",
                  bundleDependency: threatLookUpBundle.bundleDependency || null,
                  bundleActivate: threatLookUpBundle.bundleActivate ?? true,
                  blocks: threatLookUpBundle.requests.map((req: any) => ({
                    id: req.requestId,
                    response: "",
                  })),

                  finalRequestData: {
                    request: (threatLookUpBundle.requests || []).map(
                      (req: any) => {
                        // Map backend auth type to frontend format
                        let authType = req.auth?.type || "none";
                        if (authType === "basic_auth") authType = "basic";
                        else if (authType === "bearer_token")
                          authType = "bearer";
                        else if (authType === "jwt_token")
                          authType = "jwtBearer";
                        else if (authType === "api_key") authType = "api-key";
                        // authorization stays as authorization

                        return {
                          ...req,
                          auth: {
                            ...req.auth,
                            type: authType,
                          },
                          body:
                            req.body_type === "x-www-form-urlencoded" &&
                            typeof req.body === "object"
                              ? ""
                              : req.body || "",
                          formData:
                            req.body_type === "x-www-form-urlencoded" &&
                            typeof req.body === "object"
                              ? Object.entries(req.body || {}).map(
                                  ([key, value]) => ({
                                    key,
                                    value: String(value),
                                    enabled: true,
                                  }),
                                )
                              : [],
                          bodyType:
                            req.body_type === "x-www-form-urlencoded"
                              ? "form-urlencoded"
                              : req.body_type || "none",
                          rawBodyType: req.raw_type || "text",
                        };
                      },
                    ),
                    variables:
                      threatLookUpBundle.bundleConfigVariables?.map(
                        (v: any) => ({
                          id: Date.now() + Math.random(),
                          name: v.name,
                          sourcePath: v.sourcePath,
                          sourceBundleId: threatLookUpBundle.bundleId,
                          sourceRequestId:
                            threatLookUpBundle.requests?.[0]?.requestId || 0,

                          type: "string",
                          variableType: "Threat Lookup",
                        }),
                      ) || [],
                    responses: {},
                  },
                  response: {},
                  isResponse: false,
                },
              ],
            };
          }
          return item;
        });
      }

      //  If Threat Lookup doesn't exist, add it with full data
      const updated = [
        ...prev,
        {
          name: "Threat Lookup",
          data: [
            {
              id: threatLookUpBundle.bundleId,
              title: threatLookUpBundle.bundleTitle || "Threat Lookup Bundle",
              description: threatLookUpBundle.bundleDescription || "",
              bundleDependency: threatLookUpBundle.bundleDependency || null,
              bundleActivate: threatLookUpBundle.bundleActivate ?? true,
              blocks: threatLookUpBundle.requests.map((req: any) => ({
                id: req.requestId,
                response: "",
              })),
              finalRequestData: {
                request:
                  threatLookUpBundle.requests?.map((req: any) => {
                    // Map backend auth type to frontend format
                    let authType = req.auth?.type || "authorization";
                    if (authType === "basic_auth") authType = "basic";
                    else if (authType === "bearer_token") authType = "bearer";
                    else if (authType === "jwt_token") authType = "jwtBearer";
                    else if (authType === "api_key") authType = "api-key";
                    // authorization stays as authorization

                    return {
                      requestId: req.requestId,
                      url: req.url,
                      method: req.method || "GET",
                      params: req.params || [],
                      headers: req.headers || [],
                      auth: {
                        ...(req.auth || {}),
                        type: authType,
                        token: req.auth?.token || "",
                        username: req.auth?.username || "",
                        password: req.auth?.password || "",
                      },
                      body:
                        req.body_type === "x-www-form-urlencoded" &&
                        typeof req.body === "object"
                          ? ""
                          : req.body || "",
                      formData:
                        req.body_type === "x-www-form-urlencoded" &&
                        typeof req.body === "object"
                          ? Object.entries(req.body || {}).map(
                              ([key, value]) => ({
                                key,
                                value: String(value),
                                enabled: true,
                              }),
                            )
                          : [],
                      bodyType:
                        req.body_type === "x-www-form-urlencoded"
                          ? "form-urlencoded"
                          : req.body_type || "none",
                      rawBodyType: req.raw_type || "text",
                      isLoop: req.isLoop || false,
                      loopVariable: req.loopVariable || "",
                      loopSourcePath: req.loopSourcePath || "",
                      loopSourceRequest: req.loopSourceRequest || null,
                      loopSourceRequestBundle:
                        req.loopSourceRequestBundle || null,
                      checkpoint: req.checkpoint || {
                        produces: { enabled: false, sourcePath: "" },
                        consumes: { enabled: false, paramTemplate: "" },
                      },
                      flag: req.flag || 0,
                      plugin_id: req.plugin_id || pluginId || "",
                      ssl_verify: req.ssl_verify ?? true,
                      configuration_Id:
                        req.configuration_Id || threatLookUpBundle.bundleId,
                      mappingId: req.mappingId || "",
                      type_threatlookup: req.type_threatlookup || "",
                      originalUrl: req.url,
                    };
                  }) || [],
                variables:
                  threatLookUpBundle.bundleConfigVariables?.map((v: any) => ({
                    id: Date.now() + Math.random(),
                    name: v.name,
                    sourcePath: v.sourcePath,
                    sourceBundleId: threatLookUpBundle.bundleId,
                    sourceRequestId:
                      threatLookUpBundle.requests?.[0]?.requestId || 0,
                    isCommon: false,
                    isAuthentication: false,
                    type: "string",
                    variableType: "Threat Lookup",
                  })) || [],
                responses: {},
              },
              response: {},
              isResponse: false,
            },
          ],
        },
      ];
      return sortLogicTypes(updated);
    });

    //  Also update definedVariables with threat lookup variables
    if (threatLookUpBundle.bundleConfigVariables) {
      dispatch({
        type: MeshPluginActions.SET_DEFINED_VARIABLES,
        payload: (prevVars: DefinedVariable[]) => {
          const newVariables = threatLookUpBundle.bundleConfigVariables.map(
            (v: any) => ({
              id: v.id,
              name: v.name,
              sourcePath: v.sourcePath,
              sourceBundleId: threatLookUpBundle.bundleId,
              sourceRequestId: threatLookUpBundle.requests?.[0]?.requestId || 0,
              isCommon: false,
              isAuthentication: false,
              type: "string",
              variableType: "Threat Lookup",
              value: undefined,
            }),
          );

          // Filter out duplicates
          const existingNames = new Set(prevVars.map((v: any) => v.name));
          const uniqueNewVars = newVariables.filter(
            (v: any) => !existingNames.has(v.name),
          );

          return [...prevVars, ...uniqueNewVars];
        },
      });
    }

    // Update threat lookup map if there are requests
    if (threatLookUpBundle.requests && threatLookUpBundle.requests.length > 0) {
      const newThreatLookupMap: Record<number, string> = {};
      threatLookUpBundle.requests.forEach((req: any) => {
        if (req.type_threatlookup) {
          newThreatLookupMap[req.requestId] = req.type_threatlookup;
        }
      });
      setThreatLookupMap(newThreatLookupMap);
    }
  }, [threatLookUpBundle, setLogicType, dispatch, pluginId]);

  // mutation for delete bundle
  const commonDeleteApiMutation = useMutation({
    mutationFn: (payload: any) =>
      commonDeleteApi(
        payload.plugin_id,
        payload.delete_type,
        payload.bundle_uid,
        payload.request_uid,
        payload.variable_uid,
      ),

    onMutate: () => {
      setLoading(true);
    },
    onSettled: () => {
      setLoading(false);
    },

    onSuccess: () => {
      // Invalidate and refetch the logic page data
      queryClient.invalidateQueries(["fetchLogicPageData", pluginId]);

      const bundleTitle =
        logicType
          ?.find((lt) => lt.name === selectedLogicName)
          ?.data.find((b: any) => b.id === deleteBundleId)?.title || "";

      const description = deleteLogicTypeName
        ? `Bundles, requests and variables of "${deleteLogicTypeName}" have been deleted successfully.`
        : `Bundle "${bundleTitle}" and its variables have been deleted successfully.`;

      toast({
        variant: "success",
        title: "Deleted Successfully!",
        description,
        duration: 3000,
      });

      setIsDeleteDialogOpen(false);
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: "Delete failed",
        description: "Failed to delete the bundle.",
        duration: 3000,
      });
    },
  });

  // mutation delete datalist
  const deleteDatalistMutation = useMutation({
    mutationFn: (payload: any) =>
      deleteDatalistMapping(payload.datalist_title, payload.plugin_id),
    onMutate: () => {
      setLoading(true);
    },
    onSettled: () => {
      setLoading(false);
    },
    onSuccess: () => {
      // Invalidate and refetch the logic page data
      queryClient.invalidateQueries(["fetchLogicPageData", pluginId]);

      // Clean up local state for this datalist
      setMappingSaveData((prevData: any) => {
        const updated = { ...prevData };
        Object.keys(updated).forEach((bundleId) => {
          updated[bundleId] = updated[bundleId].filter(
            (item: any) => item.dataListName !== pendingDeleteTitle,
          );
        });
        return updated;
      });

      toast({
        variant: "success",
        title: "Datalist Deleted Successfully!",
        duration: 3000,
      });
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: "Delete failed",
        description: "Failed to delete the datalist.",
        duration: 3000,
      });
    },
  });

  // mutation for delete mapping
  const deleteMappingMutation = useMutation({
    mutationFn: (payload: any) =>
      deleteSaveLogMapping(payload.log_uid, payload.plugin_id),
    onMutate: () => {
      setLoading(true);
    },
    onSettled: () => {
      setLoading(false);
    },
    onSuccess: () => {
      // Invalidate and refetch the logic page data
      queryClient.invalidateQueries(["fetchLogicPageData", pluginId]);

      // Clean up local state for this mapping
      setMappingSaveData((prevData: any) => {
        const updated = { ...prevData };
        Object.keys(updated).forEach((bundleId) => {
          updated[bundleId] = updated[bundleId].filter(
            (item: any) => item.logUid !== pendingDeleteLogUid,
          );
        });
        return updated;
      });

      toast({
        variant: "success",
        title: " Mapping Deleted Successfully!",
        duration: 3000,
      });
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: "Delete failed",
        description: "Failed to delete the mapping.",
        duration: 3000,
      });
    },
  });

  const { mutateAsync: submitLogicPage, isLoading } = useMutation(
    (payload: LogicPagePayload) => saveLogicPageInfo(pluginId ?? "", payload),
  );

  // Check if the selected logic type has existing data
  const handleAddLogic = () => {
    if (!selectedLogicName || !title.trim()) return;

    if (bundleEditId) {
      // Editing existing bundle logic
      const updatedLogic = logicType?.map((logic) => {
        if (logic.name === selectedLogicName) {
          return {
            ...logic,
            data: logic.data.map((item: any) => {
              if (item.id === bundleEditId) {
                return {
                  ...item,
                  title: title.trim(),
                  description: description.trim(),
                  bundleCronTime: croneBundleTime.trim(),
                  bundleDependency: bundleDependency || null,
                };
              }
              return item;
            }),
          };
        }
        return logic;
      });
      setLogicType?.(updatedLogic);
      setShowAddLogicForm(false);
      setBundleEditId(null);
      setTitle("");
      setDescription("");
      setCroneBundleTime("");
      setBundleDependency("");
      return;
    }

    // flat map all the bundles to check for duplicate titles
    const allBundles = logicType?.flatMap((logic) => logic.data) || [];

    const duplicateTitle = allBundles.find(
      (item: any) =>
        item.title.trim().toLowerCase() === title.trim().toLowerCase(),
    );

    if (duplicateTitle) {
      toast({
        variant: "destructive",
        title: "Duplicate Title",
        description: `A logic block with the title "${title.trim()}" already exists. Please use a unique title for prevention of confusion.`,
        duration: 5000,
      });
      return;
    }

    const currentLogicType = logicType?.find(
      (logic) => logic.name === selectedLogicName,
    );

    // Check for duplicate title
    const isDuplicate = currentLogicType?.data?.some(
      (item: any) =>
        item.title.trim().toLowerCase() === title.trim().toLowerCase(),
    );

    if (isDuplicate) {
      toast({
        variant: "destructive",
        title: "Duplicate Title",
        description:
          "A logic block with this title already exists. Please use a unique title.",
        duration: 3000,
      });
      return;
    }

    const newLogUid = Date.now() + Math.floor(Math.random() * 10000);

    const updatedLogic = logicType?.map((logic) => {
      if (logic.name === selectedLogicName) {
        return {
          ...logic,
          data: [
            ...(logic.data || []),
            {
              id: newLogUid,
              // logUid: newLogUid,
              title: title.trim(),
              description: description.trim(),
              bundleCronTime: croneBundleTime.trim(),
              bundleDependency: bundleDependency || null,
              blocks: [{ id: 0, response: "" }],
              finalRequestData: { request: [], variables: [], responses: {} },
              response: {},
              isResponse: false,
            },
          ],
        };
      }
      return logic;
    });

    // To select the new Logic right after creation
    setSelectedLogicBlockId(newLogUid);
    setLogicType?.(updatedLogic);
    setShowAddLogicForm(false);
    setTitle("");
    setDescription("");
    setCroneBundleTime("");
    setBundleDependency("");
  };

  // Function to open the add logic form
  const openAddLogicForm = () => {
    setShowAddLogicForm(true);
  };

  // Function to close the add logic form
  const closeAddLogicForm = () => {
    setTitle("");
    setDescription("");
    setShowAddLogicForm(false);
    setCroneBundleTime("");
    setBundleDependency("");
    setBundleEditId(null);
  };

  // Function to select a logic item (for potential future use)
  const handleSelectLogic = (idToSelect: number) => {
    setSelectedLogicBlockId(idToSelect);
  };

  const selectedLogicBlock: any = currentLogic?.data?.find(
    (item: any) => item?.id === selectedLogicBlockId,
  );

  // Auto-select the first logic block if only one exists
  React.useEffect(() => {
    if (currentLogic?.data?.length == 1) {
      setSelectedLogicBlockId(currentLogic?.data[0]?.id || null);
    }
  }, [currentLogic]);

  // Auto-select the first logic type if none is selected
  React.useEffect(() => {
    if (
      !selectedLogicName &&
      logicType &&
      logicType.length &&
      logicType?.length > 0
    ) {
      dispatch({
        type: MeshPluginActions.SET_SELECTED_LOGIC_NAME,
        payload: logicType[0].name,
      });
    }
  }, [logicType, logicType?.length]);

  // Auto select first logic block when logic type changes
  React.useEffect(() => {
    if (currentLogic?.data?.length && currentLogic?.data?.length > 0) {
      setSelectedLogicBlockId(currentLogic?.data[0]?.id || null);
    }
  }, [selectedLogicName]);

  // Restore the previously selected tab after save/refetch settles.
  React.useEffect(() => {
    const pendingName = pendingSelectedLogicNameRef.current;
    if (!pendingName || !logicType?.length) return;

    const tabExists = logicType.some((logic) => logic.name === pendingName);
    if (!tabExists) {
      pendingSelectedLogicNameRef.current = null;
      return;
    }

    if (selectedLogicName !== pendingName) {
      dispatch({
        type: MeshPluginActions.SET_SELECTED_LOGIC_NAME,
        payload: pendingName,
      });
    }

    pendingSelectedLogicNameRef.current = null;
  }, [logicType, selectedLogicName, dispatch]);

  // Normalization functions
  const normalizeDatalist = (d: any) => ({
    ...d,
    requestId: d.requestId ?? null,
    mappingDataList: (d.mappingDataList || []).map((f: any) => ({
      fieldId: f.fieldId,
      rawFieldName: f.rawFieldName,
      mappingLsField: f.mappingLsField,
      column_type: f.column_type,
      data_type: f.data_type,
      display_name: f.display_name ?? f.displayName ?? f.rawFieldName ?? "",
    })),
  });

  // Normalization function for mappings
  const normalizeMapping = (m: any) => ({
    logUid: m.logUid ?? "",
    logTitile: m.logTitile ?? "",
    logType: m.logType ?? "",
    logCategory: m.logCategory ?? "",
    log_ls_product_type: m.log_ls_product_type ?? [],
    logCronFrequency: m.logCronFrequency ?? 0,
    mappingFields: (m.mappingFields ?? []).map((f: any) => ({
      fieldId: f.fieldId,
      rawFieldName: f.rawFieldName,
      mappingLsField: f.mappingLsField,
      display_name: f.display_name ?? f.displayName ?? "",
      type: f.type,
      category: f.category,
      is_time: f.is_time ?? false,
    })),
    requestId: m.requestId ?? null,
  });

  // Function to construct the payload for saving logic page
  const constructPayload = () => {
    const LogicBundles = logicType
      ?.flatMap((bundle) =>
        (bundle?.data ?? [])
          .filter(
            (log: any) =>
              (log?.finalRequestData?.request?.length ?? 0) > 0 ||
              (log?.mappingFields?.length ?? 0) > 0,
          )
          .map((log: any) => {
            // bundleId from finalRequestData variables or from state
            const bundleId =
              log?.id ??
              log?.finalRequestData?.request?.[0]?.configuration_Id ??
              selectedLogicBlockId ??
              Date.now() + Math.floor(Math.random() * 10000);

            // Safely get mappings that belong only to this bundleId
            const bundleMappings = Object.entries(mappingSaveData || {})
              .filter(([key]) => Number(key) === bundleId)
              .flatMap(([, arr]) =>
                arr.filter(
                  (m: any) => m?.bundleId === bundleId || !m?.bundleId,
                ),
              );

            // Get previous mappings from fetched data
            const fetchedBundle =
              fetchedLogicPageData?.data?.LogicBundles?.find(
                (b: any) => b.bundleId === bundleId,
              );

            // Get bundleActivate status from fetched data, default to true
            const bundleActivate =
              fetchedBundle?.bundleActivate ?? log.bundleActivate ?? true;

            const previousMappings = Array.isArray(fetchedBundle?.mappings)
              ? fetchedBundle.mappings
              : Array.isArray(log?.mappings)
                ? log.mappings
                : [];

            const newMappings =
              bundleMappings.length > 0
                ? bundleMappings
                    ?.filter((m: any) => m.logType === "parquet")
                    .map((mapping: any) => ({
                      logUid: mapping?.logUid ?? "",
                      logTitile: mapping?.logTitile ?? "",
                      logType: mapping?.logType ?? "",
                      logCategory: mapping?.logCategory ?? "",
                      log_ls_product_type: mapping?.log_ls_product_type ?? [],
                      logCronFrequency: mapping?.logCronFrequency ?? 0,
                      mappingFields: (mapping?.mappingFields ?? []).map(
                        (f: any) => ({
                          fieldId: f.fieldId,
                          rawFieldName: f.rawFieldName,
                          mappingLsField: f.mappingLsField,
                          display_name: f.display_name,
                          type: f.type,
                          category: f.category,
                          is_time: f.is_time ?? false,
                        }),
                      ),
                      requestId: mapping?.requestId ?? "",
                    }))
                : [];

            const normalizedPreviousMappings =
              previousMappings.map(normalizeMapping);
            const normalizedNewMappings = newMappings.map(normalizeMapping);
            const mappings =
              normalizedNewMappings.length > 0
                ? normalizedNewMappings
                : normalizedPreviousMappings;

            // Get previous datalists from fetched data
            const previousDatalists = Array.isArray(fetchedBundle?.datalists)
              ? fetchedBundle.datalists
              : Array.isArray(log?.datalists)
                ? log.datalists
                : [];

            const newDatalists =
              bundleMappings.length > 0
                ? bundleMappings
                    .filter((m: any) => m.logType === "datalist")
                    .map((m: any) => ({
                      datalistUid:
                        m.logUid ||
                        `${m.dataListName
                          .toLowerCase()
                          .replace(/\s+/g, "_")}_${Date.now()}`,
                      dataTitile: m.dataListName || "",
                      requestId: m.requestId,
                      logType: m.logType || "",
                      cronTimeType: m.cronType || "",
                      typeDatalist: m.typeDatalist || "",
                      mappingDataList: (m.mappingFields || []).map(
                        (f: any) => ({
                          fieldId: f.fieldId,
                          rawFieldName: f.rawFieldName,
                          mappingLsField: f.mappingLsField,
                          column_type: f.column_type,
                          data_type: f.data_type,
                          display_name: f.display_name,
                        }),
                      ),
                    }))
                : [];
            const normalizedPreviousDatalists =
              previousDatalists.map(normalizeDatalist);
            const normalizedNewDatalists = newDatalists.map(normalizeDatalist);

            // Merge previous and new datalists, avoiding duplicates by datalistUid
            const datalistMap = new Map();

            // Add previous datalists first
            normalizedPreviousDatalists.forEach((dl: any) => {
              if (dl.datalistUid) {
                datalistMap.set(dl.datalistUid, dl);
              }
            });

            // Add new datalists (will overwrite if same UID exists)
            normalizedNewDatalists.forEach((dl: any) => {
              if (dl.datalistUid) {
                datalistMap.set(dl.datalistUid, dl);
              }
            });

            const datalists = Array.from(datalistMap.values());
            // Deduplicate requests based on requestId, url, and method
            const uniqueRequests = Array.isArray(log?.finalRequestData?.request)
              ? log.finalRequestData.request.filter(
                  (req: any, idx: number, arr: any[]) =>
                    arr.findIndex(
                      (r: any) => r.requestId === req.requestId,
                      // &&
                      // r.url === req.url &&
                      // r.method === req.method,
                    ) === idx,
                )
              : [];

            return {
              bundleId,
              bundleTitle: log.title || "Untitled",
              bundleDescription: log.description || "",
              bundleCronTime: log.bundleCronTime || "",
              bundleDependency: log.bundleDependency || null,
              bundleType: getBundleTypeEnum(bundle?.name),
              bundleActivate: bundleActivate,
              bundleConfigVariables: (log.finalRequestData?.variables || [])
                .filter((v: any) => v.sourceBundleId == bundleId)
                .map((v: any) => ({
                  name: v.name,
                  sourcePath: v.sourcePath,
                })),
              mappings,
              datalists,
              threat_lookup:
                getBundleTypeEnum(bundle?.name) === "threat_lookup"
                  ? Object.values(threatLookupMap)
                  : [],
              requests: uniqueRequests?.map((req: any) => {
                // Map each request to its own mapping by requestId.
                const mappingForRequest = mappings.find(
                  (m: any) => String(m.requestId) === String(req?.requestId),
                );

                // For datalist bundles, include all datalists; otherwise filter by requestId
                const datalistsForRequest = datalists.filter(
                  (d: any) => Number(d.requestId) === Number(req.requestId),
                );

                const datalistId = datalistsForRequest.map(
                  (d: any) => d.datalistUid,
                );

                const requestPayload: any = {
                  requestId: req.requestId ?? 0,
                  url: req.originalUrl || req.url || "",
                  method: req.method ?? "GET",
                  params: (() => {
                    // If queryParams exist, build params from them
                    if (req.queryParams && Array.isArray(req.queryParams)) {
                      const validParams = req.queryParams
                        .filter((p: any) => p.enabled && (p.key || p.value))
                        .map((p: any) => ({
                          key: p.key || "",
                          value: p.value || "",
                        }));

                      if (validParams.length > 0) {
                        return {
                          type: "params",
                          parameter: validParams,
                        };
                      }

                      // If queryParams exists but all are disabled/empty, return none
                      return {
                        type: "none",
                        parameter: [],
                      };
                    }

                    // Fallback to existing params or default (only for legacy data without queryParams)
                    return req.params &&
                      req.params.type &&
                      (req.params.type === "params" ||
                        req.params.type === "none")
                      ? req.params
                      : {
                          type: "none",
                          parameter: [],
                        };
                  })(),
                  pagination: (() => {
                    // Build pagination from paginationType and paginationFormData if available
                    const type =
                      req.paginationType || req.pagination?.type || "none";

                    // If pagination object already exists with valid type, use it directly
                    if (
                      req.pagination &&
                      req.pagination.type &&
                      req.pagination.type !== "none"
                    ) {
                      return req.pagination;
                    }

                    if (!type || type === "none" || type === "") {
                      return { type: "none" };
                    }

                    // Normalize backend type format (remove _pagination suffix for comparison)
                    const normalizedType = type.replace(/_pagination$/, "");

                    // Get the pagination form data
                    const formData = req.paginationFormData || [];
                    const enabledRows = formData.filter(
                      (row: any) => row.paginationEnable,
                    );

                    if (enabledRows.length === 0) {
                      return { type: "none" };
                    }

                    // Get the first enabled row (we'll support single row for now)
                    const row = enabledRows[0];

                    // Build parameter object based on pagination type (backend expects object, not array)
                    if (normalizedType === "offset_limit") {
                      const offsetKey =
                        row.categoryTypeKey === "custom"
                          ? row.categoryTypeCustom
                          : row.categoryTypeKey;
                      const limitKey =
                        row.paginationTypeKey === "custom"
                          ? row.paginationTypeCustom
                          : row.paginationTypeKey;

                      if (
                        offsetKey &&
                        row.categoryTypeValue &&
                        limitKey &&
                        row.paginationTypeValue
                      ) {
                        const result = {
                          type: "offset_limit_pagination",
                          parameter: {
                            offset_key: offsetKey,
                            offset_value: row.categoryTypeValue,
                            limit_key: limitKey,
                            limit_value: row.paginationTypeValue,
                          },
                        };

                        return result;
                      }
                    } else if (
                      normalizedType === "page_page_size" ||
                      normalizedType === "page_size"
                    ) {
                      const pageKey =
                        row.categoryTypeKey === "custom"
                          ? row.categoryTypeCustom
                          : row.categoryTypeKey;
                      const pageSizeKey =
                        row.paginationTypeKey === "custom"
                          ? row.paginationTypeCustom
                          : row.paginationTypeKey;

                      if (
                        pageKey &&
                        row.categoryTypeValue &&
                        pageSizeKey &&
                        row.paginationTypeValue
                      ) {
                        const result = {
                          type: "page_size_pagination",
                          parameter: {
                            page_key: pageKey,
                            page_value: row.categoryTypeValue,
                            per_page_key: pageSizeKey,
                            per_page_value: row.paginationTypeValue,
                          },
                        };
                        return result;
                      }
                    } else if (normalizedType === "cursor") {
                      const limitKey =
                        row.limitKey === "custom"
                          ? row.limitCustomKey
                          : row.limitKey;

                      if (
                        limitKey &&
                        row.limitValue &&
                        row.cursorKey &&
                        row.cursorValue
                      ) {
                        const result = {
                          type: "cursor_pagination",
                          parameter: {
                            limit_key: limitKey,
                            limit_value: row.limitValue,
                            cursor_key: row.cursorKey,
                            cursor_value: row.cursorValue,
                            cursor_source_path: row.cursorSourcePath || "",
                          },
                        };
                        return result;
                      }
                    } else if (normalizedType === "token") {
                      const limitKey =
                        row.limitKey === "custom"
                          ? row.limitCustomKey
                          : row.limitKey;

                      if (
                        limitKey &&
                        row.limitValue &&
                        row.tokenKey &&
                        row.tokenValue
                      ) {
                        const result = {
                          type: "token_pagination",
                          parameter: {
                            limit_key: limitKey,
                            limit_value: row.limitValue,
                            token_key: row.tokenKey,
                            token_value: row.tokenValue,
                            token_source_path: row.tokenSourcePath || "",
                          },
                        };
                        return result;
                      }
                    } else if (normalizedType === "next_url") {
                      const limitKey =
                        row.limitKey === "custom"
                          ? row.limitCustomKey
                          : row.limitKey;

                      if (limitKey && row.limitValue && row.nextUrlKey) {
                        const result = {
                          type: "next_url_pagination",
                          parameter: {
                            limit_key: limitKey,
                            limit_value: row.limitValue,
                            next_url_key: row.nextUrlKey,
                            next_url_value: "", // Next URL comes from API response
                          },
                        };
                        return result;
                      }
                    }

                    // If validation fails or type doesn't match, return none
                    return { type: "none" };
                  })(),
                  filteration: {
                    type: req.filteration?.type || "none",
                    default_cron_start_time:
                      req.filteration?.default_cron_start_time || "none",
                    start_key: req.filteration?.start_key || "",
                    end_key: req.filteration?.end_key || "",
                    custom_date_format:
                      req?.filteration?.custom_date_format || "",
                  },
                  headers: Array.isArray(req.headers)
                    ? req.headers
                    : Object.entries(req.headers || {}).map(([key, value]) => ({
                        key,
                        value,
                      })),
                  auth: (() => {
                    const authType = req.auth?.type || "none";
                    const token = req.auth?.token || "";
                    const username = req.auth?.username || "";
                    const password = req.auth?.password || "";

                    // Helper to check if a string contains template variables
                    const hasTemplateVariable = (str: string) => {
                      return /\{\{.*?\}\}/.test(str);
                    };

                    // Check if auth type is already in backend format
                    const backendFormats = [
                      "basic_auth",
                      "bearer_token",
                      "jwt_token",
                      "api_key",
                      "authorization",
                    ];
                    const isBackendFormat = backendFormats.includes(authType);

                    // If basic auth but no credentials, set to none (unless they contain template variables)
                    if (authType === "basic" || authType === "basic_auth") {
                      const hasUsername =
                        username &&
                        (username.trim() !== "" ||
                          hasTemplateVariable(username));
                      const hasPassword =
                        password &&
                        (password.trim() !== "" ||
                          hasTemplateVariable(password));

                      if (!hasUsername && !hasPassword) {
                        return {
                          type: "none",
                          token: "",
                          username: "",
                          password: "",
                        };
                      }
                      return {
                        type: isBackendFormat
                          ? authType
                          : mapAuthTypeToBackend(authType),
                        token,
                        username,
                        password,
                      };
                    }

                    // If none type, return as none
                    if (authType === "none") {
                      return {
                        type: "none",
                        token: "",
                        username: "",
                        password: "",
                      };
                    }

                    // For other auth types (bearer, authorization, api-key, jwtBearer)
                    // If no token, set to none (unless it contains template variables)
                    const hasToken =
                      token &&
                      (token.trim() !== "" || hasTemplateVariable(token));

                    if (!hasToken) {
                      return {
                        type: "none",
                        token: "",
                        username: "",
                        password: "",
                      };
                    }

                    // Return with proper backend type (keep if already in backend format, otherwise map)
                    return {
                      type: isBackendFormat
                        ? authType
                        : mapAuthTypeToBackend(authType),
                      token,
                      username,
                      password,
                    };
                  })(),
                  body:
                    req.bodyType === "form-urlencoded"
                      ? Object.fromEntries(
                          (req.formData || [])
                            .filter((item: any) => item.enabled && item.key)
                            .map((item: any) => [item.key, item.value || ""]),
                        )
                      : req.body || "",
                  body_type:
                    req.bodyType === "form-urlencoded"
                      ? "x-www-form-urlencoded"
                      : req.bodyType || "none",
                  ...((req.bodyType || "none") === "raw" && {
                    raw_type: req.rawBodyType || "text",
                  }),
                  isLoop: !!req.isLoop,
                  loopVariable: req?.loopVariable ?? "",
                  loopSourcePath: req?.loopSourcePath ?? "",
                  loopSourceRequest: req?.loopSourceRequest ?? null,
                  loopSourceRequestBundle: req?.loopSourceRequestBundle ?? null,
                  checkpoint: {
                    produces: {
                      enabled: req.checkpoint?.produces?.enabled || false,
                      sourcePath: req.checkpoint?.produces?.sourcePath || "",
                    },
                    consumes: {
                      enabled: req.checkpoint?.consumes?.enabled || false,
                      paramTemplate:
                        req.checkpoint?.consumes?.paramTemplate || "",
                    },
                  },
                  flag: req.flag ?? 0,
                  plugin_id: pluginId ?? "",
                  ssl_verify: req.ssl_verify ?? true,
                  configuration_Id: bundleId,
                  mappingId:
                    getBundleTypeEnum(bundle?.name) === "parquet"
                      ? (mappingForRequest?.logUid ?? "")
                      : req?.mappingId || "",
                  datalistId,
                  type_threatlookup:
                    getBundleTypeEnum(bundle?.name) === "threat_lookup"
                      ? (threatLookupMap[req.requestId] ?? "")
                      : "",
                };
                return requestPayload;
              }),
            };
          }),
      )
      .filter(Boolean);

    const variableMap = new Map<string | number, any>();

    logicType?.forEach((bundle) => {
      bundle?.data?.forEach((log: any) => {
        log?.finalRequestData?.variables?.forEach((v: any) => {
          const id = v?.id;
          if (!id || variableMap.has(id)) return;

          variableMap.set(id, {
            id,
            name: v?.name,
            scope: v?.isCommon ? "global" : "local",
            type: v?.type || "string",
            variableType: v?.variableType,
            sourceBundleId: parseInt(v?.sourceBundleId) || Date.now(),
            sourceRequestId: v?.sourceRequestId ?? 0,
            sourcePath: v?.sourcePath,
            isCommon: v?.isCommon ?? false,
            isAuthentication: v?.isAuthentication ?? false,
          });
        });
      });
    });

    // clear all the mappingSaveData after constructing payload

    const variables = Array.from(variableMap.values());

    return {
      variables,
      LogicBundles,
      is_edit: alreadySaved ? true : false,
    };
  };

  // Function to handle saving all logics (for demonstration, just logs to console)
  const handleSaveLogics = async () => {
    const selectedLogicBeforeSave = selectedLogicName;
    pendingSelectedLogicNameRef.current = selectedLogicBeforeSave || null;
    const payload: any = constructPayload();

    if (!payload.LogicBundles || payload.LogicBundles.length === 0) {
      toast({
        variant: "destructive",
        title: "No Logic to Save",
        description: "There are no logic bundles with data to save.",
      });
      return;
    }

    // Validate all request bodies before saving
    const validationErrors: string[] = [];

    for (const bundle of payload.LogicBundles) {
      for (const request of bundle.requests || []) {
        // Validate body if it's raw type and has content (check snake_case from payload)
        if (request.body_type === "raw" && request.body) {
          const validation = validateBodyContent(
            request.body,
            request.raw_type || "text",
          );

          if (!validation.isValid) {
            const errorMsg = `Bundle "${bundle.bundleTitle}" → Request #${request.requestId}: ${validation.error}`;
            validationErrors.push(errorMsg);
          }
        }
      }
    }

    // If there are validation errors, show them and prevent save
    if (validationErrors.length > 0) {
      const errorDescription =
        validationErrors.length === 1
          ? validationErrors[0]
          : `Found ${validationErrors.length} validation error(s):\n\n${validationErrors.map((err, idx) => `${idx + 1}. ${err}`).join("\n")}`;

      toast({
        variant: "destructive",
        title: "Body Validation Failed",
        description: errorDescription,
        duration: 8000,
      });

      return;
    }

    // Validate custom filtration format
    for (const bundle of payload.LogicBundles) {
      for (const request of bundle.requests || []) {
        if (request.filteration?.type === "custom_date_format") {
          const customFormat = request.filteration?.custom_date_format || "";

          // Check if custom format is empty
          if (!customFormat || customFormat.trim() === "") {
            toast({
              variant: "destructive",
              title: "Invalid Custom Date Format",
              description: `The custom filtration format in "${bundle.bundleTitle}" (Request #${request.requestId}) cannot be empty. Please build a format using date (YYYY/YY, MM, DD) or time (HH, mm, ss) components.`,
              duration: 5000,
            });
            return;
          }

          // Check if format includes at least some valid date or time components
          const hasYear =
            customFormat.includes("YYYY") || customFormat.includes("YY");
          const hasMonth = customFormat.includes("MM");
          const hasDay = customFormat.includes("DD");

          // Check for any time components (lowercase mm = minutes, not Month)
          const hasTime =
            customFormat.includes("HH") || // 24-hour format
            customFormat.includes("hh") || // 12-hour format
            customFormat.includes("mm") || // minutes (lowercase)
            customFormat.includes("ss"); // seconds

          // Allow formats with either complete date (Year + Month + Day) OR any time components
          const hasCompleteDate = hasYear && hasMonth && hasDay;
          const hasValidFormat = hasCompleteDate || hasTime;

          if (!hasValidFormat) {
            toast({
              variant: "destructive",
              title: "Invalid Custom Date Format",
              description: `The custom filtration format in "${bundle.bundleTitle}" (Request #${request.requestId}) must include either a complete date (Year, Month, and Day) or time components. Please build a valid format.`,
              duration: 5000,
            });
            return;
          }
        }
      }
    }

    // Clear validation errors if all passed

    try {
      await submitLogicPage(payload);
      dispatch({
        type: MeshPluginActions.SET_IS_SAVED_SECTION,
        payload: (prev: { name: string; isSaved: boolean }[]) =>
          prev.map((section: { name: string; isSaved: boolean }) =>
            section.name === "logic" ? { ...section, isSaved: true } : section,
          ),
      });
      await queryClient.invalidateQueries(["fetchLogicPageData", pluginId]);

      // Keep the current tab stable after refetch; avoids unexpected jump to Action.
      if (selectedLogicBeforeSave) {
        dispatch({
          type: MeshPluginActions.SET_SELECTED_LOGIC_NAME,
          payload: selectedLogicBeforeSave,
        });
      }

      toast({
        variant: "success",
        title: "Saved",
        description: "Logic page submitted successfully.",
      });
    } catch (error: any) {
      console.error("Save Logic Error:", error);
      let errorMsg = "An error occurred while saving the logic page.";

      if (error?.response?.data) {
        const data = error.response.data;

        if (typeof data === "string") {
          errorMsg = data;
        }
        //  If invalid input AND details exist → show details message
        else if (
          data.message === "invalid input" &&
          Array.isArray(data.details) &&
          data.details.length > 0
        ) {
          errorMsg = data.details
            .map((detail: any) => detail.msg || detail.message)
            .join(", ");
        } else if (data.message) {
          errorMsg = data.message;
        } else if (data.error) {
          errorMsg =
            typeof data.error === "string"
              ? data.error
              : data.error.message || JSON.stringify(data.error);
        } else if (Array.isArray(data.details) && data.details.length > 0) {
          errorMsg = data.details
            .map((detail: any) => detail.msg || detail.message)
            .join(", ");
        } else if (data.detail) {
          errorMsg =
            typeof data.detail === "string"
              ? data.detail
              : JSON.stringify(data.detail);
        } else {
          errorMsg = JSON.stringify(data);
        }
      } else if (error?.message) {
        errorMsg = error.message;
      }

      toast({
        variant: "destructive",
        title: "Save Failed",
        description: errorMsg,
        duration: 3000,
      });
    }
  };

  // Function to delete a bundle
  const handleDeleteLogic = (idToDelete: number) => {
    setDeleteBundleId(idToDelete);
    setIsDeleteDialogOpen(true);
  };

  // Delete bundle after confirmation
  const deleteBundle = () => {
    // delete from state if unsaved bundle
    if (!deleteBundleId || !pluginId || !selectedLogicName) return;

    const isSavedInServer = fetchedLogicPageData?.data?.LogicBundles?.some(
      (bundle: any) => bundle.bundleId == deleteBundleId,
    );

    if (!isSavedInServer) {
      const updatedLogic = logicType?.map((logic) => {
        if (logic.name === selectedLogicName) {
          return {
            ...logic,
            data: logic.data.filter((item: any) => item.id !== deleteBundleId),
          };
        }
        return logic;
      });

      setLogicType?.(updatedLogic);

      // Remove variables associated with this bundle
      const updatedVariables = definedVariables?.filter(
        (variable) => variable.sourceBundleId !== deleteBundleId,
      );
      dispatch({
        type: MeshPluginActions.SET_DEFINED_VARIABLES,
        payload: updatedVariables || [],
      });

      setIsDeleteDialogOpen(false);
      setDeleteBundleId(null);

      // select the previous bundle after deletion
      if (selectedLogicBlockId === deleteBundleId) {
        const currentLogicType = logicType?.find(
          (logic) => logic.name === selectedLogicName,
        );
        if (
          currentLogicType?.data?.length &&
          currentLogicType.data.length > 0
        ) {
          const lastIndex = currentLogicType.data.length - 1;
          const activeBundleId = currentLogicType.data[lastIndex - 1].id;
          handleSelectLogic(activeBundleId);
        } else {
          setSelectedLogicBlockId(null);
        }
      }

      const bundleTitle =
        logicType
          ?.find((lt) => lt.name === selectedLogicName)
          ?.data.find((b: any) => b.id === deleteBundleId)?.title || "";

      const description = deleteLogicTypeName
        ? `Bundles, requests and variables of "${deleteLogicTypeName}" have been deleted successfully.`
        : `Bundle "${bundleTitle}" and its variables have been deleted successfully.`;

      toast({
        variant: "success",
        title: "Deleted Successfully!",
        description,
        duration: 3000,
      });
    } else {
      // call the delete api mutation if the data is already saved
      commonDeleteApiMutation.mutate({
        plugin_id: pluginId,
        delete_type: "bundle",
        bundle_uid: deleteBundleId,
        request_uid: "",
        variable_uid: "",
      });
      const updatedLogic = logicType?.map((logic) => {
        if (logic.name === selectedLogicName) {
          return {
            ...logic,
            data: logic.data.filter((item: any) => item.id !== deleteBundleId),
          };
        }
        return logic;
      });

      setLogicType?.(updatedLogic);

      // Remove variables associated with this bundle
      const updatedVariables = definedVariables?.filter(
        (variable) => variable.sourceBundleId !== deleteBundleId,
      );
      dispatch({
        type: MeshPluginActions.SET_DEFINED_VARIABLES,
        payload: updatedVariables || [],
      });

      // if the last bundle is deleted active current last bundle
      if (selectedLogicBlockId === deleteBundleId) {
        // Use updatedLogic instead of logicType to get the filtered data
        const currentLogicType = updatedLogic?.find(
          (logic) => logic.name === selectedLogicName,
        );
        if (
          currentLogicType?.data?.length &&
          currentLogicType.data.length > 0
        ) {
          // Select the last bundle from the remaining bundles
          const lastIndex = currentLogicType.data.length - 1;
          const activeBundleId = currentLogicType.data[lastIndex].id;
          handleSelectLogic(activeBundleId);
        } else {
          setSelectedLogicBlockId(null);
        }
      }

      setIsDeleteDialogOpen(false);
      setDeleteBundleId(null);
    }
  };

  // to handle other logic type
  const handleOtherLogicContinue = (type: string) => {
    setOtherType(type);

    if (type === "threatlookup" && setLogicType) {
      setLogicType((prev: LogicPagePayload[]) => {
        const exists = prev.some((item: any) => item.name === "Threat Lookup");

        if (exists) return prev;

        return [
          ...prev,
          {
            name: "Threat Lookup",
            data: [],
          },
        ];
      });
      dispatch({
        type: MeshPluginActions.SET_SELECTED_LOGIC_NAME,
        payload: "Threat Lookup",
      });
    }
    if (type === "datalist" && setLogicType) {
      setLogicType((prev: LogicPagePayload[]) => {
        const exists = prev.some((item: any) => item.name === "Data List");

        if (exists) return prev;

        return [
          ...prev,
          {
            name: "Data List",
            data: [],
          },
        ];
      });
      dispatch({
        type: MeshPluginActions.SET_SELECTED_LOGIC_NAME,
        payload: "Data List",
      });
    }

    setOpenThreadMeshLookUp(false);
  };

  // to handle threat lookup type change
  const handleThreatLookupChange = React.useCallback(
    (requestId: number, type: string) => {
      setThreatLookupMap((prev) => ({
        ...prev,
        [requestId]: type,
      }));
    },
    [],
  );

  const existingLogicTypes = React.useMemo(() => {
    return (logicType ?? []).map((l) => l.name.toLowerCase());
  }, [logicType]);

  // to edit existing bundle name and description
  const handleEditBundle = (item: any) => {
    setBundleEditId(item?.id);
    setTitle(item?.title || "");
    setDescription(item?.description || "");
    setCroneBundleTime(item?.bundleCronTime || "");
    setBundleDependency(item?.bundleDependency || "");
    setShowAddLogicForm(true);
  };

  // to delete created logic type like threat lookup and data list
  const handleDeleteLogicType = (item: string) => {
    if (item == "Threat Lookup") {
      setDeleteLogicTypeName("Threat Lookup");
      const threatLookupBundle =
        fetchedLogicPageData?.data?.LogicBundles?.filter(
          (bundle: any) => bundle.bundleType == "threat_lookup",
        );
      if (threatLookupBundle && threatLookupBundle.length > 0) {
        setIsDeleteLogicType(true);
        setIsDeleteDialogOpen(true);
      } else {
        setLogicType?.((prev: any[]) =>
          prev.filter((logic) => logic.name != "Threat Lookup"),
        );
        dispatch({
          type: MeshPluginActions.SET_SELECTED_LOGIC_NAME,
          payload: "Action",
        });
      }
    }

    if (item == "Data List") {
      setDeleteLogicTypeName("Data List");
      const dataListBundle = fetchedLogicPageData?.data?.LogicBundles?.find(
        (bundle: any) => bundle.bundleType == "datalist",
      );
      if (dataListBundle) {
        setIsDeleteLogicType(true);
        setIsDeleteDialogOpen(true);
      } else {
        setLogicType?.((prev: any[]) =>
          prev.filter((logic) => logic.name !== "Data List"),
        );
        dispatch({
          type: MeshPluginActions.SET_SELECTED_LOGIC_NAME,
          payload: "Action",
        });
      }
    }
  };

  // Function to delete logic type
  const deleteLogicType = async () => {
    if (!deleteLogicTypeName || !pluginId) return;
    setLoading(true);

    // DATA LIST (MULTIPLE BUNDLES)
    if (deleteLogicTypeName === "Data List") {
      const datalistBundles = fetchedLogicPageData?.data?.LogicBundles?.filter(
        (bundle: any) => bundle.bundleType === "datalist",
      );

      if (Array.isArray(datalistBundles)) {
        await Promise.all(
          datalistBundles.map((bundle: any) =>
            commonDeleteApiMutation.mutateAsync({
              plugin_id: pluginId,
              delete_type: "bundle",
              bundle_uid: bundle.bundleId,
              request_uid: "",
              variable_uid: "",
            }),
          ),
        );
      }

      // Remove Data List from UI state
      setLogicType?.((prev: any[]) =>
        prev.filter((logic) => logic.name !== "Data List"),
      );
    }

    // THREAT LOOKUP (SINGLE BUNDLE)
    if (deleteLogicTypeName === "Threat Lookup") {
      const threatBundle = fetchedLogicPageData?.data?.LogicBundles?.find(
        (bundle: any) => bundle.bundleType === "threat_lookup",
      );

      if (threatBundle) {
        commonDeleteApiMutation.mutate({
          plugin_id: pluginId,
          delete_type: "bundle",
          bundle_uid: threatBundle.bundleId,
          request_uid: "",
          variable_uid: "",
        });

        setLogicType?.((prev: any[]) =>
          prev.filter((logic) => logic.name !== "Threat Lookup"),
        );
      }
    }

    setMappingSaveData((prev) => {
      const updated = { ...prev };
      if (deleteBundleId !== null) {
        delete updated[deleteBundleId];
      }
      return updated;
    });

    // RESET UI STATE
    setIsDeleteDialogOpen(false);
    setIsDeleteLogicType(false);
    setDeleteLogicTypeName(null);
    // Update selected logic name if the deleted type was selected
    if (selectedLogicName === deleteLogicTypeName) {
      const nextLogicName =
        logicType?.find((l) => l.name !== deleteLogicTypeName)?.name ||
        "Action";
      dispatch({
        type: MeshPluginActions.SET_SELECTED_LOGIC_NAME,
        payload: nextLogicName,
      });
    }
    setDeleteLogicTypeName("Action");
    setLoading(false);
  };

  // delete the dataList / Mapping
  const handleDeleteDataListMapping = (title?: string, logUid?: string) => {
    setPendingDeleteTitle(title || null);
    setPendingDeleteLogUid(logUid || null);
    setIsDeleteDatalistConfirmOpen(true);
  };

  // Execute the actual deletion after confirmation
  const confirmDeleteDataListMapping = () => {
    if (selectedLogicName == "Data List" && pendingDeleteTitle) {
      deleteDatalistMutation.mutate(
        {
          datalist_title: pendingDeleteTitle,
          plugin_id: pluginId,
        },
        {
          onSettled: () => {
            // Reset states after mutation completes
            setIsDeleteDatalistConfirmOpen(false);
            setPendingDeleteTitle(null);
            setOpenViewDatalist(false);
          },
        },
      );
    }

    if (selectedLogicName == "Save Logs" && pendingDeleteLogUid) {
      deleteMappingMutation.mutate(
        {
          log_uid: pendingDeleteLogUid || "",
          plugin_id: pluginId,
        },
        {
          onSettled: () => {
            // Reset states after mutation completes
            setIsDeleteDatalistConfirmOpen(false);
            setPendingDeleteLogUid(null);
            setOpenViewDatalist(false);
          },
        },
      );
    }
  };

  return (
    <FormWrapper
      title="Query Builder"
      option="logic"
      goToPage={goToPage}
      selectedApiData={selectedApiData}
      definedVariables={definedVariables}
      setDefinedVariables={(vars) =>
        dispatch({
          type: MeshPluginActions.SET_DEFINED_VARIABLES,
          payload: vars,
        })
      }
      selectedLogicBlockId={selectedLogicBlockId}
      logicType={logicType}
      uiPages={uiPages}
      configurationData={configurationData}
      pluginId={pluginId}
    >
      {/* Confirmation Dialog */}
      {isDeleteDialogOpen && (
        <Confirmation
          text={
            "Are you sure you want to delete ? This action cannot be undone."
          }
          confirmAction={isDeleteLogicType ? deleteLogicType : deleteBundle}
          rejectAction={() => setIsDeleteDialogOpen(false)}
          confirmText="Yes, delete it!"
          rejectText="Cancel"
          show={isDeleteDialogOpen}
          closeDialog={() => setIsDeleteDialogOpen(false)}
        />
      )}

      {/* Confirmation Dialog for Datalist/Mapping Deletion */}
      {isDeleteDatalistConfirmOpen && (
        <Confirmation
          text={
            selectedLogicName === "Data List"
              ? `Are you sure you want to delete the datalist "${pendingDeleteTitle}"? This action cannot be undone.`
              : `Are you sure you want to delete this mapping? This action cannot be undone.`
          }
          confirmAction={confirmDeleteDataListMapping}
          rejectAction={() => {
            setIsDeleteDatalistConfirmOpen(false);
            setPendingDeleteTitle(null);
            setPendingDeleteLogUid(null);
          }}
          confirmText="Yes, delete it!"
          rejectText="Cancel"
          show={isDeleteDatalistConfirmOpen}
          closeDialog={() => {
            setIsDeleteDatalistConfirmOpen(false);
            setPendingDeleteTitle(null);
            setPendingDeleteLogUid(null);
          }}
        />
      )}

      {/* Datalist , Threat lookup addition dialog */}
      <OtherLogicTypeDialog
        open={openThreadMeshLookUp}
        onOpenChange={setOpenThreadMeshLookUp}
        otherType={otherType}
        setOtherType={setOtherType}
        onContinue={handleOtherLogicContinue}
        disabledTypes={existingLogicTypes}
      />

      {/* loader */}
      <PageLoader show={isLoading || loading} />

      <div className="w-full flex flex-col min-h-full">
        <Tabs
          value={selectedLogicName || ""}
          onValueChange={(value) => {
            dispatch({
              type: MeshPluginActions.SET_SELECTED_LOGIC_NAME,
              payload: value,
            });
            setSelectedLogicBlockId(null);
          }}
          className="w-full "
        >
          <div className="sticky top-0 z-30 flex items-center gap-3 justify-between bg-primary-background backdrop-blur-sm pb-2">
            <TabsList className="bg-background rounded-xl flex w-fit justify-start border-b pb-1 h-full ">
              {logicType?.map((logic) => {
                const isActive =
                  (logic.name === "Data List" &&
                    selectedLogicName === "Data List") ||
                  (logic.name === "Threat Lookup" &&
                    selectedLogicName === "Threat Lookup");

                return (
                  <TabsTrigger
                    key={logic.name}
                    value={logic.name}
                    className="data-[state=active]:bg-primary data-[state=active]:text-background rounded-lg px-4 py-2 text-sm min-w-[110px] flex items-center gap-3"
                  >
                    {logic.name}

                    {(logic.name == "Threat Lookup" ||
                      logic.name == "Data List") && (
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteLogicType(logic.name);
                        }}
                        className={`
                           cursor-pointer text-primary hover:text-destructive
                          transition-all duration-200 ease-out
                          ${isActive ? "opacity-100 scale-100" : "opacity-0 scale-75 pointer-events-none"}
                        `}
                      >
                        <Trash
                          size={14}
                          className="text-background hover:taxt-muted-foreground"
                        />
                      </span>
                    )}
                  </TabsTrigger>
                );
              })}
            </TabsList>
            <div className="mr-4">
              <div
                onClick={() => setOpenThreadMeshLookUp(true)}
                className="rounded-lg h-full text-sm flex items-center hover:border-secondary cursor-pointer text-primaryBlue hover:text-muted-foreground whitespace-nowrap "
              >
                <CirclePlus size={15} className="mr-1" />
                Add Logic Type
              </div>
            </div>
          </div>

          {logicType?.map((logic) => {
            const isSelected = logic.name === selectedLogicName;
            const logicBlocks = logic.data || [];
            return (
              <TabsContent key={logic.name} value={logic.name} className="ml-1">
                {isSelected && (
                  <>
                    {/* Add Logic Button */}
                    <div className="flex items-center gap-3 justify-between">
                      <div className="flex items-center gap-3">
                        <button
                          className={`w-fit flex gap-1 items-center text-sm 
                      ${
                        selectedLogicName === "Threat Lookup" &&
                        logicBlocks.length == 1
                          ? "text-muted-foreground cursor-not-allowed"
                          : "text-primaryBlue hover:text-muted-foreground cursor-pointer"
                      }
                      `}
                          onClick={openAddLogicForm}
                          disabled={
                            selectedLogicName === "Threat Lookup" &&
                            logicBlocks.length == 1
                          }
                        >
                          <CirclePlus size={15} /> Create {logic.name} Bundle
                        </button>
                        {logic.name === "Threat Lookup" && (
                          <InfoTooltip text="Creating multiple Threat Lookup bundles is not allowed." />
                        )}
                      </div>
                    </div>

                    {/* Add Logic Dialog */}
                    {showAddLogicForm && (
                      <CreateBundleDialog
                        open={showAddLogicForm}
                        onClose={closeAddLogicForm}
                        title={title}
                        setTitle={setTitle}
                        description={description}
                        setDescription={setDescription}
                        selectedLogicName={logic.name}
                        handleAddLogic={handleAddLogic}
                        closeAddLogicForm={closeAddLogicForm}
                        croneBundleTime={croneBundleTime}
                        setCroneBundleTime={setCroneBundleTime}
                        bundleDependency={bundleDependency}
                        setBundleDependency={setBundleDependency}
                        logicType={logicType}
                        isEdit={bundleEditId}
                        plugin_id={pluginId}
                        bundle_id={bundleEditId || selectedLogicBlockId}
                        initialBundleActivate={
                          bundleEditId
                            ? (fetchedLogicPageData?.data?.LogicBundles?.find(
                                (b: any) => b.bundleId === bundleEditId,
                              )?.bundleActivate ?? true)
                            : true
                        }
                      />
                    )}

                    <ViewMappingsDialog
                      open={openViewDatalist}
                      onClose={() => setOpenViewDatalist(false)}
                      title={
                        selectedLogicName === "Save Logs"
                          ? "Save Logs Mappings"
                          : "Data List Mappings"
                      }
                      mappingGroups={
                        selectedLogicName === "Save Logs"
                          ? viewSaveLogsData
                          : viewDatalistData.map((dl: any) => ({
                              uid: dl.datalistUid,
                              title: dl.dataTitile,
                              subtitle: `Cron: ${dl.cronTimeType} • Type: ${dl.logType}`,
                              fields: dl.fields,
                            }))
                      }
                      onDeleteGroup={(title, uid) =>
                        handleDeleteDataListMapping(title, uid)
                      }
                    />

                    {/* Logic Block Cards */}
                    {logicBlocks.length > 0 ? (
                      <div className="mt-4">
                        <div className="flex justify-between items-center ">
                          <div className="flex justify-start items-center gap-3 w-full flex-wrap text-sm">
                            {logicBlocks.map((item: any) => (
                              <div
                                key={item.id}
                                className={`border w-[200px] p-3 rounded-lg bg-muted flex justify-between items-start cursor-pointer border-muted  ${
                                  item.id === selectedLogicBlockId
                                    ? `bg-primary shadow-lg border-primary`
                                    : "hover:border-secondary border-secondary text-secondary "
                                } 
                                 ${item?.bundleActivate === false && "!bg-gray-400 border-dashed !border-gray-500 "}
                                 `}
                                onClick={() => handleSelectLogic(item.id || 0)}
                              >
                                <div
                                  className={`flex justify-between w-full items-center text-background`}
                                >
                                  <div className="flex flex-col gap-1 flex-1">
                                    <p
                                      className={`flex items-center gap-1 ${item.id === selectedLogicBlockId ? "font-semibold" : "text-muted-foreground "} `}
                                    >
                                      <Group
                                        size={15}
                                        className={`${item.id === selectedLogicBlockId ? "font-semibold text-lightGray" : "text-muted-foreground "}`}
                                      />
                                      <span className={`max-w-[80px] truncate`}>
                                        {item.title}
                                      </span>
                                    </p>
                                  </div>

                                  {item.id === selectedLogicBlockId && (
                                    <div className="flex gap-1 items-center">
                                      {/* Status Badge */}
                                      {item?.bundleActivate === false && (
                                        <span
                                          className="text-[10px] px-1 rounded-full border  text-background w-fit ml-1"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleEditBundle(item);
                                          }}
                                        >
                                          Inactive
                                        </span>
                                      )}
                                      <SquarePen
                                        size={14}
                                        className={`text-background hover:text-muted-foreground`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleEditBundle(item);
                                        }}
                                      />
                                      {item?.bundleActivate !== false && (
                                        <Trash2
                                          size={15}
                                          className={`text-background hover:text-muted-foreground`}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteLogic(item.id || 0);
                                          }}
                                        />
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                          {(selectedLogicName == "Save Logs" ||
                            selectedLogicName == "Data List") && (
                            <div
                              className="flex items-center text-sm mr-4 gap-1 cursor-pointer whitespace-nowrap px-3 py-2 rounded-md bg-lightGray hover:bg-lightGray hover:text-primary"
                              onClick={() => {
                                setOpenViewDatalist(true);
                              }}
                            >
                              <FolderOpen size={15} />
                              <span>
                                {" "}
                                View{" "}
                                {selectedLogicName == "Save Logs"
                                  ? "mappings"
                                  : "datalists"}
                              </span>
                            </div>
                          )}
                        </div>

                        <p className="text-sm mt-4 ml-1 text-muted-foreground ">
                          Logic blocks of bundle :{" "}
                          <span className="text-primary">
                            {selectedLogicBlock?.title}
                          </span>
                        </p>
                      </div>
                    ) : (
                      logicBlocks.length === 0 && (
                        <div className="flex flex-1 items-center justify-center min-h-[40vh]">
                          <FallBackUi
                            title="No bundles found !"
                            description={`Create a new bundle for ${selectedLogicName} to continue.`}
                          />
                        </div>
                      )
                    )}

                    {/* Logic Block Content */}
                    {logicBlocks.length > 0 && selectedLogicBlock && (
                      <div className="border border-transparent bg-background p-3 mt-2">
                        <QueryChainManager
                          setSelectedApiData={setSelectedApiData}
                          setDefinedVariables={(vars) =>
                            dispatch({
                              type: MeshPluginActions.SET_DEFINED_VARIABLES,
                              payload: vars,
                            })
                          }
                          definedVariables={definedVariables}
                          logicType={logicType}
                          setLogicType={setLogicType}
                          selectedLogicBlockId={selectedLogicBlockId}
                          pluginId={pluginId}
                          setMappingSaveData={setMappingSaveData}
                          mappingSaveData={mappingSaveData}
                          threatLookupMap={threatLookupMap}
                          onThreatLookupChange={handleThreatLookupChange}
                        />
                      </div>
                    )}
                  </>
                )}
              </TabsContent>
            );
          })}
        </Tabs>
        <div className="flex justify-end mt-auto sticky bottom-0 bg-primary-background pt-2 pb-2">
          {/* button for saving all the logics if there is at least one bundle in current logicType */}

          <Button
            onClick={handleSaveLogics}
            className="bg-primaryGreen hover:bg-primaryGreen text-background w-[200px]"
          >
            Save All Logics
          </Button>
        </div>
      </div>
    </FormWrapper>
  );
}

export default LogicPage;
